import {cdnImage} from '@/util/cdnImage';
import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '@/components/Ui-components/display-image';
import {borderRadius, colors, fontWeight, zIndex} from '@/styles/tokens';
const iconSetup = cdnImage('white-label-exchange-development-new/delivery-icon-setup.svg');
const iconCode = cdnImage('white-label-exchange-development-new/delivery-icon-code.svg');
const iconShield = cdnImage('white-label-exchange-development-new/delivery-icon-shield.svg');
const iconRocket = cdnImage('white-label-exchange-development-new/delivery-icon-rocket.svg');
const iconCheckGreen = cdnImage('white-label-exchange-development-new/delivery-icon-check-green.svg');
const iconRocketWhite = cdnImage('white-label-exchange-development-new/delivery-icon-rocket-white.svg');
// ─── Data ─────────────────────────────────────────────────────────────────────

const steps = [
    {
        week: 'Week 1',
        title: 'Discovery & Setup',
        icon: iconSetup,
        weDo: ['Requirements workshop', 'Environment setup', 'Infrastructure provisioning', 'Initial configuration'],
        youDo: ['Provide brand assets', 'Confirm feature scope', 'Confirm target jurisdictions'],
        milestone: {text: 'Project roadmap confirmed', dark: false}
    },
    {
        week: 'Week 2–3',
        title: 'Build & Customise',
        icon: iconCode,
        weDo: ['Apply your branding', 'Configure trading pairs', 'Integrate KYC/AML', 'Connect liquidity providers'],
        youDo: ['Review UI/UX designs', 'Provide regulatory docs', 'Approve configurations'],
        milestone: {text: 'Branded platform live in staging', dark: false}
    },
    {
        week: 'Week 4–5',
        title: 'Test & Integrate',
        icon: iconShield,
        weDo: ['Full QA testing', 'Security review', 'Performance load testing', 'Compliance QA'],
        youDo: ['User acceptance testing', 'Final feature sign-off'],
        milestone: {text: 'Platform clears all tests', dark: false}
    },
    {
        week: 'Week 6',
        title: 'Go-Live',
        icon: iconRocket,
        weDo: ['Production deployment', 'DNS setup & config', 'Monitoring configured', 'Go-live support on-call'],
        youDo: ['Announce your exchange', 'Begin user acquisition'],
        milestone: {text: 'Your exchange is live. Start earning.', dark: true}
    }
];

// ─── sx constants ─────────────────────────────────────────────────────────────

const stepIconWrapSx = {
    width: 48,
    height: 48,
    bgcolor: colors.accent.orange,
    border: '4px solid #f7faff',
    borderRadius: borderRadius['3xl'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    flexShrink: 0
} as const;

const cardSx = {
    bgcolor: colors.background.default,
    border: '1px solid #e5e7eb',
    borderRadius: borderRadius.lg,
    p: '21px',
    boxShadow: '0px 4px 6px rgba(0,0,0,0.03)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%'
} as const;

const weekBadgeSx = {
    bgcolor: '#e0f2fe',
    borderRadius: borderRadius.xl,
    px: '12px',
    py: '4px',
    display: 'inline-flex',
    alignItems: 'center'
} as const;

const greenMilestoneSx = {
    bgcolor: '#dcfce7',
    border: '1px solid #bbf7d0',
    borderRadius: borderRadius['6'],
    p: '13px',
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    mt: 'auto'
} as const;

const darkMilestoneSx = {
    bgcolor: colors.primary.deep,
    borderRadius: borderRadius['6'],
    p: '12px',
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    mt: 'auto'
} as const;

// ─── Sub-component ────────────────────────────────────────────────────────────

const BulletList = ({items}: {items: string[]}) => (
    <Box display="flex" flexDirection="column" gap="7px">
        {items.map((item, i) => (
            <Box key={i} display="flex" alignItems="flex-start" gap="8px">
                <Typography
                    component="span"
                    className="font-regular-text"
                    sx={{
                        color: colors.accent.orange,
                        fontWeight: fontWeight.bold,
                        fontSize: 14,
                        lineHeight: '16px',
                        flexShrink: 0,
                        mt: '1px'
                    }}
                >
                    •
                </Typography>
                <Typography className="font-regular-text" sx={{fontSize: 12, color: '#6b7280', lineHeight: '16.2px'}}>
                    {item}
                </Typography>
            </Box>
        ))}
    </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const WhiteLabelDeliveryProcess = () => {
    return (
        <Box>
            <MUIContainer>
                {/* Heading */}
                <Box textAlign="center" maxWidth={1047} margin="0 auto" mb={{xs: 6, md: 8}}>
                    <Typography variant="h2" component="h2" mb={3} fontSize={{xs: 32, sm: 52}} fontWeight={800} className="font-headers">
                        From Contract to Live Exchange in 6 Weeks — Here&apos;s Exactly How
                    </Typography>
                    <Typography
                        className="font-regular-text"
                        sx={{fontSize: 16, lineHeight: '28px', color: '#000', maxWidth: 941, mx: 'auto'}}
                    >
                        We don&apos;t just hand over software and wish you luck. Our structured 6-week delivery process has been refined
                        across 15+ exchange launches — so you know exactly what&apos;s happening at every stage, and what&apos;s expected
                        from your team.
                    </Typography>
                </Box>

                {/* Steps */}
                <Box sx={{position: 'relative'}}>
                    {/* Horizontal connector line */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 23,
                            left: '12.5%',
                            right: '12.5%',
                            height: 2,
                            bgcolor: colors.neutral.borderLight,
                            zIndex: zIndex.base
                        }}
                    />

                    <Grid container spacing={3}>
                        {steps.map((step, idx) => (
                            <Grid item xs={12} sm={6} md={3} key={idx}>
                                <Box display="flex" flexDirection="column" alignItems="center" height="100%">
                                    {/* Step icon */}
                                    <Box sx={stepIconWrapSx} mb={3}>
                                        <DisplayImage
                                            src={step.icon}
                                            alt={step.title}
                                            width={24}
                                            height={24}
                                            style={{width: 24, height: 24}}
                                        />
                                    </Box>

                                    {/* Card */}
                                    <Box sx={cardSx} width="100%">
                                        {/* Week badge + title */}
                                        <Box>
                                            <Box sx={weekBadgeSx} mb={1.5}>
                                                <Typography
                                                    className="font-regular-text"
                                                    sx={{
                                                        fontWeight: fontWeight.semibold,
                                                        fontSize: 12,
                                                        color: '#0369a1',
                                                        letterSpacing: '0.5px',
                                                        textTransform: 'uppercase',
                                                        lineHeight: '18px'
                                                    }}
                                                >
                                                    {step.week}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                className="font-headers"
                                                sx={{
                                                    fontWeight: fontWeight.bold,
                                                    fontSize: 16,
                                                    color: colors.text.primary,
                                                    lineHeight: '20.8px',
                                                    mb: 2.5
                                                }}
                                            >
                                                {step.title}
                                            </Typography>

                                            {/* We do / You do columns */}
                                            <Grid container spacing={1.5} sx={{mb: 3}}>
                                                <Grid item xs={6}>
                                                    <Typography
                                                        className="font-headers"
                                                        sx={{
                                                            fontWeight: fontWeight.semibold,
                                                            fontSize: 13,
                                                            color: colors.text.primary,
                                                            lineHeight: '19.5px',
                                                            mb: '7px'
                                                        }}
                                                    >
                                                        We do:
                                                    </Typography>
                                                    <BulletList items={step.weDo} />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography
                                                        className="font-headers"
                                                        sx={{
                                                            fontWeight: fontWeight.semibold,
                                                            fontSize: 13,
                                                            color: colors.text.primary,
                                                            lineHeight: '19.5px',
                                                            mb: '7px'
                                                        }}
                                                    >
                                                        You do:
                                                    </Typography>
                                                    <BulletList items={step.youDo} />
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        {/* Milestone badge */}
                                        <Box sx={step.milestone.dark ? darkMilestoneSx : greenMilestoneSx}>
                                            <Box flexShrink={0} mt="1px">
                                                <DisplayImage
                                                    src={step.milestone.dark ? iconRocketWhite : iconCheckGreen}
                                                    alt=""
                                                    width={18}
                                                    height={18}
                                                    style={{width: 18, height: 18}}
                                                />
                                            </Box>
                                            <Typography
                                                className="font-headers"
                                                sx={{
                                                    fontWeight: fontWeight.semibold,
                                                    fontSize: 13,
                                                    color: step.milestone.dark ? '#fff' : '#166534',
                                                    lineHeight: '18.2px'
                                                }}
                                            >
                                                {step.milestone.text}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(WhiteLabelDeliveryProcess);
